import type { ComponentType, ReactNode, SVGProps } from 'react';
import { cn } from '../lib/cn';

/**
 * MarketingCard — "Switchboard-Modernist" content card: monospace eyebrow with
 * optional index, primary icon chip, display-type title, muted body.
 *
 * Theme-token based (bg-card / text-card-foreground / text-muted-foreground /
 * border-border / shadow-glow-primary), so the SAME component renders the dark
 * marketing look on the marketing site and a lighter card on the portal's light
 * theme — no per-app forking. Hoisted from marketing/src into @afrilink/ui
 * (2026-07-08) as the single shared definition.
 *
 * Three variants:
 * - default  → icon + title + description (the common grid item)
 * - featured → adds the four primary corner brackets + glow + ring
 * - minimal  → compact hairline row with leading icon, no padded chrome
 */

export type MarketingCardVariant = 'default' | 'featured' | 'minimal';

export interface MarketingCardProps {
  /** Lucide icon component (or any React SVG component) — pass the component, not an instance. */
  icon?: ComponentType<SVGProps<SVGSVGElement>>;
  /** Optional 1-based position; renders "01/" prefix on the eyebrow if set. */
  index?: number;
  /** Mono eyebrow label above the title — defaults to "ITEM" when not provided. Set to null to hide. */
  eyebrow?: ReactNode;
  /** Card title — display font, bold. */
  title: string;
  /** Body copy below the title. */
  description?: ReactNode;
  /** Use 'featured' for the corner-bracket + glow treatment, 'minimal' for compact rows. */
  variant?: MarketingCardVariant;
  /** Optional children rendered after the description (extra CTA, link, sub-list). */
  children?: ReactNode;
  className?: string;
}

export function MarketingCard({
  icon: Icon,
  index,
  eyebrow,
  title,
  description,
  variant = 'default',
  children,
  className,
}: MarketingCardProps) {
  // Minimal variant — compact hairline row, no padded card shell.
  if (variant === 'minimal') {
    return (
      <div
        className={cn(
          'group flex items-center gap-3 rounded-sm border border-border bg-card px-4 py-3.5 transition-colors duration-200 hover:border-primary/40 hover:bg-muted',
          className,
        )}
      >
        {Icon && (
          <Icon
            className="h-4 w-4 shrink-0 text-primary"
            aria-hidden="true"
          />
        )}
        <span className="text-sm font-medium leading-tight text-card-foreground">{title}</span>
      </div>
    );
  }

  const isFeatured = variant === 'featured';

  return (
    <article
      className={cn(
        'group relative isolate flex h-full flex-col overflow-hidden rounded-md',
        'bg-card',
        'transition-all duration-300 ease-out hover:-translate-y-1',
        isFeatured
          ? 'brackets-static border border-primary/40 ring-1 ring-primary/20 shadow-glow-primary-sm hover:shadow-glow-primary'
          : 'brackets-hover border border-border hover:border-primary/40 hover:shadow-glow-primary',
        className,
      )}
    >
      {/* Featured top-edge gradient hairline */}
      {isFeatured && (
        <div
          aria-hidden="true"
          className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary to-transparent"
        />
      )}

      <div className="flex flex-grow flex-col p-6">
        {/* Eyebrow row — mono index + label */}
        {eyebrow !== null && (
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            {index !== undefined && (
              <span className="text-primary/80">{String(index).padStart(2, '0')}/</span>
            )}
            {eyebrow ?? 'Item'}
          </div>
        )}

        {/* Icon — primary accent chip */}
        {Icon && (
          <div className="mt-3 flex h-9 w-9 items-center justify-center rounded-sm border border-primary/30 bg-primary/[0.07] transition-colors duration-200 group-hover:border-primary/50 group-hover:bg-primary/15">
            <Icon
              className="h-4 w-4 text-primary transition-transform duration-200 group-hover:scale-110"
              aria-hidden="true"
            />
          </div>
        )}

        {/* Title */}
        <h3 className="font-display mt-3 text-lg font-bold leading-tight tracking-tight text-card-foreground">
          {title}
        </h3>

        {/* Description */}
        {description && (
          <p className="mt-2 pb-2 text-sm leading-relaxed text-muted-foreground">{description}</p>
        )}

        {children && <div className="mt-3">{children}</div>}
      </div>
    </article>
  );
}
